'use client';

import { useEffect, useRef } from 'react';
import { getAuthSession, reportAuthSessionFailure } from '@/app/utils/auth-session-api';
import {
  clearModernAuthApproval,
  currentModernAuthApproval,
  modernAuthCompletionEvent,
  modernAuthEventProperties,
} from '@/app/utils/auth-analytics';
import { useTracking } from '@/components/tracking-provider';

/**
 * Redirect-mode auth completes on a fresh document after the BFF callback.
 * Confirm the HttpOnly-cookie session before emitting the legacy-parity
 * completion event; never infer completion from the Emerge resume URL alone.
 */
export default function AuthCompletionTracker() {
  const tracker = useTracking();
  const attemptedRef = useRef(false);

  useEffect(() => {
    const approval = currentModernAuthApproval();
    if (!approval || approval.completionMode !== 'redirect' || attemptedRef.current) {
      return;
    }
    attemptedRef.current = true;

    void getAuthSession()
      .then((session) => {
        if (!session) {
          clearModernAuthApproval();
          return;
        }

        tracker.setUser(session.user.id);
        tracker.trackEvent(
          modernAuthCompletionEvent(approval.flow),
          modernAuthEventProperties(approval),
        );
        clearModernAuthApproval();
      })
      .catch((error) => {
        reportAuthSessionFailure(error, {
          source: 'auth-completion-tracker',
          operation: 'session',
        });
        // Retain the short-lived marker so a reload can report a completed
        // session after a transient BFF session-check failure.
        attemptedRef.current = false;
      });
  }, [tracker]);

  return null;
}
